import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App, Form, Input, Modal } from 'antd'
import type { RentalOrder, ServiceOrder } from '@super-mega/server/contracts/order'
import { AttachmentUpload } from '@/features/files/index.ts'
import { useTRPC } from '@/trpc/react.ts'

export function OrderNotesEditModal({
  order,
  kind,
  onClose
}: {
  order: RentalOrder | ServiceOrder
  kind: 'rental' | 'service'
  onClose: () => void
}) {
  const [open, setOpen] = useState(true)
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const [form] = Form.useForm<{ note: string; fileIds: number[] }>()
  const rental = useMutation(trpc.order.rental.updateNotes.mutationOptions())
  const service = useMutation(trpc.order.service.updateNotes.mutationOptions())
  const mutation = kind === 'rental' ? rental : service
  const save = async () => {
    try {
      const values = await form.validateFields()
      await mutation.mutateAsync({ orderId: order.id, note: values.note.trim() || null, fileIds: values.fileIds })
      await Promise.all(
        kind === 'rental'
          ? [
              cache.invalidateQueries(trpc.order.rental.list.queryFilter()),
              cache.invalidateQueries(trpc.order.rental.detail.queryFilter({ rentalOrderId: order.id }))
            ]
          : [
              cache.invalidateQueries(trpc.order.service.list.queryFilter()),
              cache.invalidateQueries(trpc.order.service.detail.queryFilter({ serviceOrderId: order.id }))
            ]
      )
      void message.success('订单信息已更新')
      setOpen(false)
    } catch (error) {
      if (error instanceof Error) {
        void message.error(error.message)
      }
    }
  }
  return (
    <Modal
      open={open}
      title={`编辑订单 · ${order.orderNo}`}
      onCancel={() => setOpen(false)}
      afterClose={onClose}
      onOk={() => void save()}
      confirmLoading={mutation.isPending}
      cancelButtonProps={{ disabled: mutation.isPending }}
      closable={!mutation.isPending}
      mask={{ closable: false }}
      okText="保存"
    >
      <p className="text-content-muted">当前状态可修改备注和附件。</p>
      <Form
        name="order-notes-edit"
        form={form}
        layout="vertical"
        disabled={mutation.isPending}
        initialValues={{ note: order.note ?? '', fileIds: order.attachments.map(({ file }) => file.id) }}
      >
        <Form.Item label="备注" name="note">
          <Input.TextArea rows={4} maxLength={5000} showCount />
        </Form.Item>
        <Form.Item label="附件" name="fileIds">
          <AttachmentUpload
            purpose={kind === 'rental' ? 'rentalAttachment' : 'serviceAttachment'}
            maxCount={20}
            initialFiles={order.attachments.map(({ file }) => ({
              id: file.id,
              name: file.originName || file.fileName,
              fileType: file.fileType,
              size: Number(file.fileSize)
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
